/**
 * autoWhiteBackground.js
 * ------------------------------------------------------------------
 * Auto-cleans a photo's studio backdrop — light gray, off-white, OR
 * solid black/dark — so every product photo matches the site's
 * pure-white theme.
 *
 * HOW IT WORKS
 * It samples the four corners of the photo to figure out what color
 * THIS particular photo's background actually is (it could be light
 * gray, off-white, or solid black — it adapts per photo rather than
 * assuming one fixed color). It then flood-fills inward from the
 * edges: any pixel connected to the border that's within color
 * distance `tolerance` of that sampled background color gets pushed
 * to pure white (#ffffff). The flood fill stops the moment it hits a
 * pixel that doesn't match, so it can't cross into the product itself
 * unless the product touches the very edge of the frame.
 *
 * LIMITATIONS (be upfront about this with the team)
 * This is NOT AI-based subject cutout. It assumes the photo has one
 * fairly uniform backdrop color touching all four corners (true for
 * almost all catalog/product photography). It won't help with a
 * colorful, patterned, or gradient lifestyle-shot background, and if
 * a corner of the frame happens to contain part of the product rather
 * than backdrop, the sample can be thrown off — that's rare for
 * centered product shots but worth knowing.
 */

/**
 * @param {string} base64  A base64 data URL (e.g. from compressImage()).
 * @param {object} [opts]
 * @param {number} [opts.tolerance=40]  Max color distance (0-441) from the
 *   sampled corner color to still count as "background". Higher = more
 *   aggressive (catches more background, higher risk of eating into the
 *   product); lower = more conservative.
 * @param {string} [opts.mimeType='image/jpeg']
 * @param {number} [opts.quality=0.92]
 * @returns {Promise<string>} cleaned base64 data URL
 */
export function autoWhitenBackground(
  base64,
  { tolerance = 40, mimeType = 'image/jpeg', quality = 0.92 } = {}
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);

        const { width, height } = canvas;
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;

        // --- Step 1: figure out what color THIS photo's backdrop is ---
        const SAMPLE = 6; // average a small block at each corner, not just 1px
        const sampleCorner = (cx, cy) => {
          let r = 0, g = 0, b = 0, n = 0;
          for (let y = cy; y < cy + SAMPLE && y < height; y++) {
            for (let x = cx; x < cx + SAMPLE && x < width; x++) {
              const o = (y * width + x) * 4;
              r += data[o]; g += data[o + 1]; b += data[o + 2]; n++;
            }
          }
          return [r / n, g / n, b / n];
        };
        const dist = (a, b) =>
          Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);

        const corners = [
          sampleCorner(0, 0),
          sampleCorner(width - SAMPLE, 0),
          sampleCorner(0, height - SAMPLE),
          sampleCorner(width - SAMPLE, height - SAMPLE),
        ];
        // Pick the corner whose color the other corners agree with most —
        // guards against one corner accidentally landing on the product.
        let bgColor = corners[0];
        let bestScore = Infinity;
        for (const c of corners) {
          const score = corners.reduce((s, o) => s + dist(c, o), 0);
          if (score < bestScore) { bestScore = score; bgColor = c; }
        }

        // --- Step 2: flood-fill inward from the edges, matching that color ---
        const total = width * height;
        const visited = new Uint8Array(total);
        const queue = new Int32Array(total);
        let qHead = 0;
        let qTail = 0;

        const matchesBg = (r, g, b) => dist([r, g, b], bgColor) <= tolerance;

        const pushIfBg = (x, y) => {
          if (x < 0 || y < 0 || x >= width || y >= height) return;
          const idx = y * width + x;
          if (visited[idx]) return;
          const o = idx * 4;
          if (matchesBg(data[o], data[o + 1], data[o + 2])) {
            visited[idx] = 1;
            queue[qTail++] = idx;
          }
        };

        for (let x = 0; x < width; x++) {
          pushIfBg(x, 0);
          pushIfBg(x, height - 1);
        }
        for (let y = 0; y < height; y++) {
          pushIfBg(0, y);
          pushIfBg(width - 1, y);
        }

        while (qHead < qTail) {
          const idx = queue[qHead++];
          const x = idx % width;
          const y = (idx / width) | 0;
          pushIfBg(x + 1, y);
          pushIfBg(x - 1, y);
          pushIfBg(x, y + 1);
          pushIfBg(x, y - 1);
        }

        // --- Step 3: push every matched background pixel to pure white ---
        for (let i = 0; i < total; i++) {
          if (visited[i]) {
            const o = i * 4;
            data[o] = 255;
            data[o + 1] = 255;
            data[o + 2] = 255;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL(mimeType, quality));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Could not load image for background cleanup'));
    img.src = base64;
  });
}
